<script setup>
import { __, __nx } from 'JIX/gettext.js';
import { ref, nextTick, computed, watch } from 'vue';
import { getNumberFormatter } from 'JIX/utils.js';
import { splitQstring, useTwoWayBinding } from 'JIX/jobsearch/utils';
import JobsearchSearchMobile from './JobsearchSearchMobile.vue';
import { useMatchesBreakpoint } from 'JIX/breakpointListener.js';
import JobsearchNewJobsearchSwitch from './JobsearchNewJobsearchSwitch.vue';
import JobsearchSearchBarGeo from './JobsearchSearchBarGeo.vue';
import JobsearchSearchBarGeneral from './JobsearchSearchBarGeneral.vue';
import JobsearchRenderQuery from './JobsearchRenderQuery.vue';


import 'icons/button-close.svg';
import { useStore } from 'vuex';

const store = useStore();

defineProps({
    formAction: {
        type: String,
        required: true,
    },
    numJobs: {
        type: Number,
        required: true,
    },
    showVersionSwitch: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const numberFormatter = getNumberFormatter();

const isDesktop = useMatchesBreakpoint('md');

const form = ref();

const searchbargeneral = ref();
const searchbargeo = ref();

function firstQueryParamSelected({ origin }) {
    searchbargeneral.value.showDropdown = false;
    if (!store.getters.selectedValues().geoareaid && (!origin || origin !== 'focusout')) {
        nextTick(() => searchbargeo.value.searchbar.focusInput());
    }
}

const selectedValues = computed(() => store.getters.selectedValues('workingQuery'));
const geoValuesSelected = computed(() => Array.isArray(selectedValues.value.geoareaid));

const generalSearchPlaceholderText = __('Hvilket job leder du efter?');
const generalSearchPlaceholderTextFilled = __('Vil du søge efter mere?');
const generalSearchPlaceholderTextCurrent = ref(generalSearchPlaceholderText);

const geoSearchPlaceholderText = __('Hvor vil du arbejde?');
const geoSearchPlaceholderTextFilled = __('Ret geografi');
const geoSearchPlaceholderTextCurrent = computed(() => geoValuesSelected.value ? geoSearchPlaceholderTextFilled : geoSearchPlaceholderText);

function generalQueryParamSelected() {
    generalSearchPlaceholderTextCurrent.value = generalSearchPlaceholderTextFilled;
}

watch(() => selectedValues.value, () => {
    if (Object.keys(selectedValues.value).length === 0){
        generalSearchPlaceholderTextCurrent.value = generalSearchPlaceholderText;
    }
});

const userInput = useTwoWayBinding('userInput');
const userInputGeo = useTwoWayBinding('userInputGeo');

async function submit() {
    let valid = true;
    const userInputTrim = userInput.value.trim();
    const userInputGeoTrim = userInputGeo.value.trim();

    if (userInputTrim) {
        splitQstring(userInputTrim).forEach(value => store.dispatch('addParam', { key: 'q', value }));
        userInput.value = '';
    }

    if (userInputGeoTrim) {
        const geoField = form.value.querySelector('[data-searchbar="geo"] input');
        geoField.setCustomValidity(__('Vælg geografi fra listen'));
        geoField.reportValidity();
        valid = false;
    }

    store.commit('processGeoParams');
    await nextTick();

    if (valid) {
        form.value.submit();
    }
}

const hiddenFields = computed(() => {
    const res = [];
    for (const [key, items] of Object.entries(store.getters.workingQuery)) {
        if (Array.isArray(items)) {
            if (key === 'q') {
                const q = items.map(x => x).join(" ");
                res.push(['qs', q]); // Transform q into qs since this is the frontpage.
            } else {
                res.push(...items.map(x => [key, x]));
            }
        }
    }
    if (store.getters.workingQuery.address && store.getters.workingQuery.radius) {
        res.push(
            ['address', store.getters.workingQuery.address],
            ['radius', store.getters.workingQuery.radius],
        );
    }

    return res;
});

watch(() => store.getters.workingQuery, () => {
    store.dispatch('fetchHitcount');
}, { deep: 2 });

function handleSearchBarGeoFocusIn() {
    searchbargeneral.value?.handleUnhandledInput({ origin: 'searchbargeofocus' });
}

function resetSearch() {
    store.commit('clearParams');
}

// TODO: Analytics events
// TODO: Form variant for super-/subcategories
// TODO: The rest of the fucking <del>owl</del><ins>form</ins>
</script>

<template>
    <form
        :action="formAction"
        method="get"
        role="search"
        autocomplete="off"
        ref="form"
        @submit.prevent="submit"
    >
        <div class="mb-4">
            <h1 class="d-none d-md-inline">
                {{ __('Søg efter nyt job') }}
            </h1>
            <h1 class="h2 d-md-none">
                {{ __nx("Søg blandt {n} job", "Søg blandt {n} job", numJobs, { n: numberFormatter.format(numJobs) }) }}
            </h1>
        </div>
        <div class="jobsearch-frontpage__fields-background" v-if="isDesktop">
            <JobsearchSearchBarGeneral
                ref="searchbargeneral"
                @suggestion-selected.once="firstQueryParamSelected"
                @suggestion-selected="generalQueryParamSelected"
                class="w-100 jobsearch-frontpage__field-container--general"
                :input-classes="'form-control form-control-lg jobsearch-frontpage__input--general'"
                @empty-enter="submit"
                :aria-label="generalSearchPlaceholderTextCurrent"
                :placeholder="generalSearchPlaceholderTextCurrent"
            />
            <JobsearchSearchBarGeo
                ref="searchbargeo"
                class="w-75 jobsearch-frontpage__field-container--geo"
                :input-classes="'form-control form-control-lg jobsearch-frontpage__input--geo'"
                @empty-enter="submit"
                :aria-label="geoSearchPlaceholderTextCurrent"
                :placeholder="geoSearchPlaceholderTextCurrent"
                @focusin="handleSearchBarGeoFocusIn"
            />
            <div class="shadow-clip w-50">
                <button class="btn btn-primary btn-lg w-100" type="submit">
                    {{ __('Søg') }}
                </button>
            </div>
        </div>
        <JobsearchSearchMobile
            v-else
            @search="submit"
            @reset-search="resetSearch"
            :input-classes="'form-control form-control-lg jobsearch-frontpage__mobile-input'"
        />
        <input
            type="hidden"
            v-for="[key, value], paramIndex in hiddenFields"
            :key="paramIndex"
            :name="key"
            :value="value"
        >
    </form>
    <JobsearchRenderQuery show-reset-search-button @reset-search="resetSearch" class="mt-4" />
    <JobsearchNewJobsearchSwitch class="mt-4" v-if="showVersionSwitch" />
</template>
